import { View, TouchableOpacity, KeyboardAvoidingView } from "react-native"
import { useSafeAreaInsets } from "react-native-safe-area-context"

import FeedbackTextInput from "@/components/input/FeedbackTextInput"
import BackButton from "@/assets/icons/back-arrow-dark.svg"
import { useUser, useUserProfileNameMutation } from "@/queries/user"
import { router } from "expo-router"
import { PrimaryButton } from "@/components/buttons/CommonButtons"
import { type Dispatch, type SetStateAction, useState } from "react"
import * as AccountEvents from "@/analytics/events/account"
import { useViewedCallback } from "@/analytics/react"
import { setStatusBarStyle } from "@/stores/statusBarStyle"
import useDeviceAttributes from "@/hooks/useDeviceAttributes"
import BFText from "@/components/standard/BFText"

interface EditNameFormProps {
  firstName: string
  firstNameError: string | undefined
  setFirstName: Dispatch<SetStateAction<string | null | undefined>>
  lastName: string
  lastNameError: string | undefined
  setLastName: Dispatch<SetStateAction<string | null | undefined>>
  disabled?: boolean
}

const EditNameForm: React.FC<EditNameFormProps> = ({
  firstName,
  setFirstName,
  firstNameError,
  lastName,
  setLastName,
  lastNameError,
  disabled = false,
}: EditNameFormProps) => {
  return (
    <View className="flex flex-col justify-start flex-1">
      <View className="flex flex-col mt-6 shrink-0">
        <View className="py-1">
          <BFText className="text-midnight-blue font-spacegrotesk">
            First Name
          </BFText>
        </View>
        <View className="py-1">
          <FeedbackTextInput
            value={firstName}
            onChange={setFirstName}
            error={firstNameError}
            disabled={disabled}
          />
        </View>
      </View>
      <View className="flex flex-col mt-6 shrink-0">
        <View className="py-1">
          <BFText className="text-midnight-blue font-spacegrotesk">
            Last Name
          </BFText>
        </View>
        <View className="py-1">
          <FeedbackTextInput
            value={lastName}
            onChange={setLastName}
            error={lastNameError}
            disabled={disabled}
          />
        </View>
      </View>
    </View>
  )
}

const onBack = () => {
  router.back()
}

export default function EditNameScreen() {
  const { data, isLoading } = useUser()
  const updateProfileNameMutation = useUserProfileNameMutation()
  const insets = useSafeAreaInsets()
  const { isWeb } = useDeviceAttributes()

  useViewedCallback(() => {
    setStatusBarStyle("dark")
    AccountEvents.accountEditNameViewed()
  })

  const [firstName, setFirstName] = useState<string | null | undefined>(
    undefined
  )

  const [firstNameError, setFirstNameError] = useState<string | undefined>(
    undefined
  )

  const [lastName, setLastName] = useState<string | null | undefined>(undefined)

  const [lastNameError, setLastNameError] = useState<string | undefined>(
    undefined
  )

  if (isLoading || !data?.me) {
    return
  }

  const { me: userProfile } = data

  const scratchFirstName = firstName ?? userProfile.firstName
  const scratchLastName = lastName ?? userProfile.lastName

  const isDisabled = isLoading || updateProfileNameMutation.isPending

  const validate = () => {
    let isValid = true
    if (
      typeof scratchFirstName === "undefined" ||
      scratchFirstName == null ||
      scratchFirstName.length === 0
    ) {
      setFirstNameError("First name is required.")
      isValid = false
    } else {
      setFirstNameError(undefined)
    }

    if (
      typeof scratchLastName === "undefined" ||
      scratchLastName == null ||
      scratchLastName.length === 0
    ) {
      setLastNameError("Last name is required.")
      isValid = false
    } else {
      setLastNameError(undefined)
    }

    return isValid
  }

  const onSave = () => {
    if (isDisabled) {
      return
    }

    if (validate()) {
      updateProfileNameMutation.mutate(
        {
          firstName: scratchFirstName,
          lastName: scratchLastName,
        },
        {
          onSuccess: () => {
            router.navigate("/account/personal-info/")
          },
          onError: (error) => {
            console.log(error)
          },
        }
      )
    }
  }

  return (
    <KeyboardAvoidingView
      className="flex flex-col justify-start flex-1 bg-white"
      behavior="padding"
      style={{
        paddingTop: isWeb ? insets.top + 10 : insets.top,
      }}
    >
      <View className="flex flex-col px-8 shrink-0">
        <TouchableOpacity testID={"back-button"} onPress={onBack}>
          <BackButton />
        </TouchableOpacity>
        <BFText
          className={`text-[24px] font-figtree-bold leading-[24px] text-midnight-blue pt-4`}
        >
          Edit Name
        </BFText>
      </View>
      <View className="flex flex-col justify-between flex-1 h-full px-8 pb-8">
        <EditNameForm
          firstName={scratchFirstName}
          setFirstName={setFirstName}
          firstNameError={firstNameError}
          lastName={scratchLastName}
          setLastName={setLastName}
          lastNameError={lastNameError}
          disabled={isDisabled}
        />
        <View className="flex flex-col flex-1 max-h-14">
          <PrimaryButton testID={"save-button"} title="Save" onPress={onSave} />
        </View>
      </View>
    </KeyboardAvoidingView>
  )
}
